'use client';

import React, { useMemo, useRef, useEffect, useState } from 'react';
import { startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, format, differenceInMinutes, startOfDay, endOfDay } from 'date-fns';
import { Plus } from 'lucide-react';
import { cn } from '@/app/components/lib/utils';
import { CALENDAR_COLOR_MAP } from './constants';
import { CalendarMiniPicker } from './CalendarMiniPicker';
import { CalendarEventDetails } from './CalendarEventDetails';
const SLOT_HEIGHT = 48; // 48px per 30 minutes
const HOURS = Array.from({
  length: 24
}, (_, i) => i);
export const CalendarDayView = ({
  currentDate,
  selectedDate,
  events = [],
  selectedEventId,
  onDateSelect,
  onEventClick,
  onSelectEvent,
  onAddEvent,
  className,
  slotProps
}) => {
  const containerRef = useRef(null);
  const [now, setNow] = useState(new Date());

  // Active day to display: selectedDate or currentDate
  const activeDay = selectedDate || currentDate;
  const isCurrentDay = isToday(activeDay);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Days of current week for mobile top switcher
  const weekDays = useMemo(() => {
    const start = startOfWeek(activeDay, {
      weekStartsOn: 1
    });
    const end = endOfWeek(activeDay, {
      weekStartsOn: 1
    });
    return eachDayOfInterval({
      start,
      end
    });
  }, [activeDay]);

  // Normalize events
  const parsedEvents = useMemo(() => {
    return events.map(ev => ({
      ...ev,
      startDate: typeof ev.start === 'string' ? new Date(ev.start) : ev.start,
      endDate: typeof ev.end === 'string' ? new Date(ev.end) : ev.end
    }));
  }, [events]);

  // Events occurring on active day
  const dayStart = startOfDay(activeDay);
  const dayEnd = endOfDay(activeDay);
  const dayEvents = useMemo(() => {
    return parsedEvents.filter(ev => {
      return ev.startDate <= dayEnd && ev.endDate >= dayStart;
    });
  }, [parsedEvents, dayStart, dayEnd]);

  // Currently inspected event (for the sidebar)
  const selectedEvent = useMemo(() => {
    if (selectedEventId) {
      const found = events.find(e => e.id === selectedEventId);
      if (found) return found;
    }
    // Default to first event on this day if any
    return dayEvents[0] || null;
  }, [selectedEventId, events, dayEvents]);

  // Positioned events with overlap calculation
  const positionedEvents = useMemo(() => {
    return dayEvents.map((ev, i) => {
      const startMinutes = Math.max(0, differenceInMinutes(ev.startDate, dayStart));
      const duration = Math.max(20, differenceInMinutes(ev.endDate, ev.startDate));
      const top = startMinutes / 30 * SLOT_HEIGHT;
      const height = Math.max(32, duration / 30 * SLOT_HEIGHT);

      // Check overlaps
      const overlapping = dayEvents.filter((other, oi) => {
        if (oi >= i) return false;
        return ev.startDate < other.endDate && ev.endDate > other.startDate;
      });
      const totalOverlapping = dayEvents.filter(other => ev.startDate < other.endDate && ev.endDate > other.startDate).length;
      return {
        ...ev,
        top,
        height,
        overlapIndex: overlapping.length,
        totalOverlapping: Math.max(1, totalOverlapping)
      };
    });
  }, [dayEvents, dayStart]);

  // Auto-scroll to earliest event of the day or 8 AM
  useEffect(() => {
    if (!containerRef.current) return;
    let earliestMinutes = 8 * 60;
    if (dayEvents.length > 0) {
      const mins = dayEvents.map(ev => ev.startDate.getHours() * 60 + ev.startDate.getMinutes());
      earliestMinutes = Math.min(...mins);
    }
    const scrollTarget = Math.max(0, earliestMinutes / 30 * SLOT_HEIGHT - SLOT_HEIGHT);
    containerRef.current.scrollTop = scrollTarget;
  }, [activeDay, dayEvents]);

  // Current time position
  const currentMinutesFromMidnight = now.getHours() * 60 + now.getMinutes();
  const currentTimeTop = currentMinutesFromMidnight / 30 * SLOT_HEIGHT;
  const daySlot = slotProps?.dayView;
  return <div {...daySlot?.root} className={cn('flex flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-900', className, daySlot?.root?.className)}>
            {/* Mobile Weekday Selector Bar (< lg) */}
            <div {...daySlot?.mobileHeader} className={cn('grid grid-cols-7 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 lg:hidden shadow-2xs', daySlot?.mobileHeader?.className)}>
                {weekDays.map(day => {
        const isSelected = isSameDay(day, activeDay);
        const isTodayDay = isToday(day);
        return <button key={day.toISOString()} type="button" onClick={() => onDateSelect?.(day)} className="flex flex-col items-center justify-center gap-1 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            <span className="text-[11px] font-semibold uppercase text-zinc-400">{format(day, 'EEE')}</span>
                            <span className={cn('flex size-6 items-center justify-center rounded-full text-xs font-bold transition-all', isSelected && 'bg-purple-600 text-white shadow-xs', isTodayDay && !isSelected && 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100', !isSelected && !isTodayDay && 'text-zinc-700 dark:text-zinc-300')}>
                                {format(day, 'd')}
                            </span>
                        </button>;
      })}
            </div>

            {/* Main Body: Left Schedule + Right Sidebar */}
            <div className="flex flex-1 overflow-hidden">
                {/* Left: Schedule Timeline */}
                <div ref={containerRef} {...daySlot?.timeline} className={cn('relative flex flex-1 overflow-y-auto select-none', daySlot?.timeline?.className)} style={{
        scrollBehavior: 'smooth'
      }}>
                    {/* Time scale column */}
                    <div {...daySlot?.timeColumn} className={cn('w-16 sm:w-20 shrink-0 border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-850/50', daySlot?.timeColumn?.className)}>
                        {HOURS.map(hour => {
            const timeDate = new Date();
            timeDate.setHours(hour, 0, 0, 0);
            const formattedTime = format(timeDate, 'h a');
            return <div key={hour} style={{
              height: `${SLOT_HEIGHT * 2}px`
            }} className="relative border-b border-zinc-100 dark:border-zinc-800/80 pr-2 text-right">
                                    <span className="-translate-y-1/2 block text-xs font-medium text-zinc-400 dark:text-zinc-500 pt-1">{formattedTime}</span>
                                </div>;
          })}
                    </div>

                    {/* Single Day Time Track */}
                    <div className="relative flex-1">
                        {HOURS.map(hour => <React.Fragment key={hour}>
                                {/* 00-30 */}
                                <div style={{
              height: `${SLOT_HEIGHT}px`
            }} onClick={() => onAddEvent?.(activeDay, hour)} className="group relative border-b border-dashed border-zinc-100 dark:border-zinc-800/60 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40 cursor-pointer transition-colors">
                                    <button type="button" className="absolute right-2 bottom-1 hidden group-hover:inline-flex size-6 items-center justify-center rounded-md bg-white dark:bg-zinc-800 text-zinc-400 hover:text-purple-600 shadow-2xs border border-zinc-200 dark:border-zinc-700" title={`Add event at ${hour}:00`}>
                                        <Plus className="size-3.5" />
                                    </button>
                                </div>

                                {/* 30-60 */}
                                <div style={{
              height: `${SLOT_HEIGHT}px`
            }} onClick={() => onAddEvent?.(activeDay, hour)} className="group relative border-b border-zinc-200/80 dark:border-zinc-800 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40 cursor-pointer transition-colors">
                                    <button type="button" className="absolute right-2 bottom-1 hidden group-hover:inline-flex size-6 items-center justify-center rounded-md bg-white dark:bg-zinc-800 text-zinc-400 hover:text-purple-600 shadow-2xs border border-zinc-200 dark:border-zinc-700" title={`Add event at ${hour}:30`}>
                                        <Plus className="size-3.5" />
                                    </button>
                                </div>
                            </React.Fragment>)}

                        {/* Positioned Event Cards */}
                        {positionedEvents.map(ev => {
            const color = ev.color || 'brand';
            const colorStyle = CALENDAR_COLOR_MAP[color] || CALENDAR_COLOR_MAP.brand;
            const widthPercent = 100 / ev.totalOverlapping;
            const leftPercent = ev.overlapIndex * widthPercent;
            const isEventSelected = selectedEvent?.id === ev.id;
            const cardSlot = daySlot?.eventCard?.(ev);
            return <div key={ev.id} onClick={e => {
              e.stopPropagation();
              onSelectEvent?.(ev);
              onEventClick?.(ev);
            }} style={{
              top: `${ev.top}px`,
              height: `${ev.height}px`,
              width: `calc(${widthPercent}% - 8px)`,
              left: `calc(${leftPercent}% + 4px)`,
              zIndex: 10 + ev.overlapIndex
            }} {...cardSlot} className={cn('absolute flex flex-col justify-start overflow-hidden rounded-xl p-3 text-xs transition-all hover:shadow-md cursor-pointer ring-1 ring-inset', colorStyle.root, isEventSelected && 'ring-2 ring-purple-600 shadow-sm', cardSlot?.className)}>
                                    <div className="flex items-center justify-between gap-1">
                                        <span className={cn('text-sm font-bold truncate leading-snug', colorStyle.label)}>{ev.title}</span>
                                        {ev.dot && <span className={cn('size-2 rounded-full shrink-0', colorStyle.dot)} />}
                                    </div>

                                    <time className={cn('text-xs mt-1 opacity-85 font-medium', colorStyle.time)}>
                                        {format(ev.startDate, 'h:mm a')} – {format(ev.endDate, 'h:mm a')}
                                    </time>

                                    {ev.description && <p className="mt-1 line-clamp-2 text-xs text-zinc-600 dark:text-zinc-400 opacity-90">{ev.description}</p>}
                                </div>;
          })}

                        {/* Current Time Indicator Line */}
                        {isCurrentDay && <div style={{
            top: `${currentTimeTop}px`
          }} {...daySlot?.currentTimeIndicator} className={cn('pointer-events-none absolute right-0 left-0 z-30 flex items-center -translate-y-1/2', daySlot?.currentTimeIndicator?.className)}>
                                <div className="size-2.5 rounded-full bg-red-500 shadow-xs -ml-1.5 ring-2 ring-white dark:ring-zinc-900" />
                                <span className="rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-2xs mr-1">
                                    {format(now, 'h:mm a')}
                                </span>
                                <div className="h-0.5 flex-1 bg-red-500 shadow-2xs" />
                            </div>}
                    </div>
                </div>

                {/* Right Sidebar (Mini Month Picker + Event Details Inspector) */}
                <aside {...daySlot?.sidebar} className={cn('hidden lg:flex w-80 xl:w-88 flex-col shrink-0 border-l border-zinc-200 dark:border-zinc-800 overflow-y-auto bg-white dark:bg-zinc-900', daySlot?.sidebar?.className)}>
                    {/* Mini Month Calendar Picker */}
                    <div className="border-b border-zinc-200 dark:border-zinc-800 p-2">
                        <CalendarMiniPicker selectedDate={activeDay} onSelectDate={date => onDateSelect?.(date)} events={events} />
                    </div>

                    {/* Event Details Card */}
                    <div className="flex-1">
                        <CalendarEventDetails event={selectedEvent} slotProps={slotProps} />
                    </div>
                </aside>
            </div>
        </div>;
};